import { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';
import configService from '../../../../shared/services/configService';
import type { Service, VehiculeConfig } from '../../../../shared/types/config';
import type { CategorieMission, PanelInterventionDetail } from '../InterventionsCreate';

/**
 * Hook extrait de InterventionsCreate.tsx :
 * - charge catégories mission + config building types
 * - charge services + véhicules
 * - charge le détail panel pour pré-remplir le formulaire en mode édition
 */

interface UseInterventionFormDataOpts {
  isEditMode: boolean;
  editingId: number;
  setEvolutionTaux: (v: Record<number, number>) => void;
  setEvolutionExtra: (v: Record<number, { victimes: number; hostiles: number; radio: string }>) => void;
  setVehiculeQuantites: (v: Record<number, number>) => void;
  setScenarioKind: (v: 'aucun' | 'tuto' | 'exceptionnelle') => void;
  setConditionBatimentRows: (
    v: { config_building_id: number; quantite_min: number }[]
  ) => void;
}

export function useInterventionFormData(opts: UseInterventionFormDataOpts) {
  const {
    isEditMode,
    editingId,
    setEvolutionTaux,
    setEvolutionExtra,
    setVehiculeQuantites,
    setScenarioKind,
    setConditionBatimentRows,
  } = opts;

  const [categoriesMission, setCategoriesMission] = useState<CategorieMission[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [configBuildingTypes, setConfigBuildingTypes] = useState<
    Array<{ id: number; code: string; nom: string; service_id: number | null }>
  >([]);
  const [services, setServices] = useState<Service[]>([]);
  const [vehicules, setVehicules] = useState<VehiculeConfig[]>([]);
  const [loadingVehicules, setLoadingVehicules] = useState(true);

  const [panelEditDetail, setPanelEditDetail] = useState<PanelInterventionDetail | null>(null);
  const [loadingEditDetail, setLoadingEditDetail] = useState(false);
  const [detailLoadError, setDetailLoadError] = useState<string | null>(null);

  // Charger le scénario à éditer
  useEffect(() => {
    if (!isEditMode) {
      setPanelEditDetail(null);
      setDetailLoadError(null);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setDetailLoadError("Token d'authentification manquant");
      return;
    }
    let cancelled = false;
    const loadDetail = async () => {
      setLoadingEditDetail(true);
      setDetailLoadError(null);
      try {
        const apiUrl = getApiBaseUrl();
        const res = await fetch(`${apiUrl}/api/cta/interventions/${editingId}`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        const ct = res.headers.get('content-type');
        if (!ct || !ct.includes('application/json')) {
          if (!cancelled) setDetailLoadError('Réponse serveur invalide.');
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        if (!data.success || !data.data) {
          setDetailLoadError(data.error || 'Scénario introuvable.');
          setPanelEditDetail(null);
          return;
        }
        const det = data.data as PanelInterventionDetail;
        setPanelEditDetail(det);
        const evo: Record<number, number> = {};
        const evoExtra: Record<number, { victimes: number; hostiles: number; radio: string }> = {};
        for (const e of det.evolutions || []) {
          if (e.id > 0) {
            evo[e.id] = e.taux;
            evoExtra[e.id] = {
              victimes: e.victimes_ajoutees ?? 0,
              hostiles: e.hostiles_ajoutees ?? 0,
              radio: e.radio ?? '',
            };
          }
        }
        setEvolutionTaux(evo);
        setEvolutionExtra(evoExtra);
        const vq: Record<number, number> = {};
        for (const v of det.vehicules_requis || []) {
          if (v.id_vehicule > 0) vq[v.id_vehicule] = v.quantite;
        }
        setVehiculeQuantites(vq);
        if (det.is_tuto) setScenarioKind('tuto');
        else if (det.is_exceptionnelle) setScenarioKind('exceptionnelle');
        else setScenarioKind('aucun');

        const spec = det.conditions_spec;
        if (spec?.batiments?.length) {
          setConditionBatimentRows(
            spec.batiments.map((b) => ({
              config_building_id: b.config_building_id,
              quantite_min: Math.max(1, b.quantite_min || 1),
            }))
          );
        } else {
          setConditionBatimentRows([{ config_building_id: 0, quantite_min: 1 }]);
        }
      } catch (e: any) {
        if (!cancelled) setDetailLoadError(e?.message || 'Erreur de chargement.');
      } finally {
        if (!cancelled) setLoadingEditDetail(false);
      }
    };
    void loadDetail();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editingId]);

  // Charger options statiques (catégories mission, types de bâtiments)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const apiUrl = getApiBaseUrl();
    const headers = { Authorization: `Bearer ${token}` };

    const load = async () => {
      setLoadingOptions(true);
      try {
        const [catRes, buildingsRes] = await Promise.all([
          fetch(`${apiUrl}/api/cta/categories-mission`, { headers, credentials: 'include' }),
          fetch(`${apiUrl}/api/cta/config-building-types`, { headers, credentials: 'include' }),
        ]);
        if (catRes.ok) {
          const catData = await catRes.json();
          if (catData.success && Array.isArray(catData.data)) {
            setCategoriesMission(catData.data);
          }
        }
        if (buildingsRes.ok) {
          const bData = await buildingsRes.json();
          if (bData.success && Array.isArray(bData.data)) {
            setConfigBuildingTypes(bData.data);
          }
        }
      } catch (e) {
        console.warn('Chargement des options:', e);
      } finally {
        setLoadingOptions(false);
      }
    };
    load();
  }, []);

  // Charger services + véhicules config
  useEffect(() => {
    const loadServicesVehicules = async () => {
      setLoadingVehicules(true);
      try {
        const [servRes, vehRes] = await Promise.all([
          configService.getServices(),
          configService.getVehiculeConfigs(),
        ]);
        if (servRes.success && servRes.data) setServices(servRes.data);
        if (vehRes.success && vehRes.data) setVehicules(vehRes.data);
      } catch (e) {
        console.warn('Chargement services / véhicules:', e);
      } finally {
        setLoadingVehicules(false);
      }
    };
    loadServicesVehicules();
  }, []);

  return {
    categoriesMission,
    loadingOptions,
    configBuildingTypes,
    services,
    vehicules,
    loadingVehicules,
    panelEditDetail,
    loadingEditDetail,
    detailLoadError,
  };
}
